/**
 * Sales sources — orders, customers, products, returns and the demand
 * forecasts built on order history. Gated to the `sales` module.
 *
 * Revenue here is *order* revenue (what was sold, by order date) as
 * opposed to the booked P&L revenue under Finance. The two differ by
 * timing, refunds and channel fees, which is exactly why both exist.
 */

import type { SalesChannelKind } from '@/types/database';
import type { DatasetBag, OrderChannelKind, OrderLite } from '../datasets';
import type { BreakdownDef, ForecastSourceDef, MetricDef, TableDef } from '../catalogTypes';
import type { ForecastPoint, SeriesPoint } from '../types';
import { monthKey } from '../series';
import {
  dailyCount,
  dailySeries,
  groupCount,
  groupSum,
  inWindow,
  monthlySeries,
  titleCase,
  toSeries,
  toSlices,
} from './helpers';

const MODULE = 'sales';

export const CHANNEL_LABELS: Record<OrderChannelKind, string> = {
  shopify: 'Shopify',
  amazon: 'Amazon',
  b2b_portal: 'B2B portal',
  manual: 'Manual / phone',
  other: 'Other',
};

const OPEN_STATUSES = new Set(['pending_payment', 'allocated', 'picking', 'packed', 'on_hold']);

function allOrders(bag: DatasetBag): OrderLite[] {
  return bag.orders?.orders ?? [];
}

/** Orders that count toward revenue — everything but cancellations. */
function activeOrders(bag: DatasetBag): OrderLite[] {
  return allOrders(bag).filter((o) => o.status !== 'cancelled');
}

function ordersIn(bag: DatasetBag, start: string, end: string): OrderLite[] {
  return activeOrders(bag).filter((o) => inWindow(o.day, start, end));
}

function lineRevenue(order: OrderLite): number {
  return order.lines.reduce((s, l) => s + l.unitPriceCents * l.quantity, 0);
}

function lineUnits(order: OrderLite): number {
  return order.lines.reduce((s, l) => s + l.quantity, 0);
}

function revenueDaily(bag: DatasetBag): SeriesPoint[] {
  return dailySeries(activeOrders(bag), (o) => o.day, (o) => o.totalCents);
}

function ordersDaily(bag: DatasetBag): SeriesPoint[] {
  return dailyCount(activeOrders(bag), (o) => o.day);
}

function unitsDaily(bag: DatasetBag): SeriesPoint[] {
  return dailySeries(activeOrders(bag), (o) => o.day, lineUnits);
}

function customerKey(order: OrderLite): string | null {
  return order.customerKey;
}

function customerLabel(order: OrderLite): string {
  return order.customerLabel ?? order.customerKey ?? 'Guest';
}

function distinctCustomers(orders: OrderLite[]): number {
  return new Set(orders.map(customerKey).filter((k): k is string => !!k)).size;
}

function notConfigured(bag: DatasetBag): string | null {
  return bag.orders && !bag.orders.configured && bag.orders.orders.length === 0
    ? 'No sales channels are connected yet.'
    : null;
}

function channelMetric(kind: SalesChannelKind): MetricDef[] {
  const label = CHANNEL_LABELS[kind];
  return [
    {
      id: `sales.revenue_${kind}`,
      kind: 'metric',
      label: `${label} revenue`,
      group: 'Sales',
      description: `Order revenue from ${label} channels.`,
      module: MODULE,
      requires: ['orders'],
      unit: 'currency',
      direction: 'up',
      grain: 'day',
      agg: 'sum',
      nonNegative: true,
      series: (bag) =>
        dailySeries(
          activeOrders(bag).filter((o) => o.channelKind === kind),
          (o) => o.day,
          (o) => o.totalCents,
        ),
    },
    {
      id: `sales.orders_${kind}`,
      kind: 'metric',
      label: `${label} orders`,
      group: 'Sales',
      description: `Orders placed through ${label} channels.`,
      module: MODULE,
      requires: ['orders'],
      unit: 'count',
      direction: 'up',
      grain: 'day',
      agg: 'sum',
      nonNegative: true,
      series: (bag) => dailyCount(activeOrders(bag).filter((o) => o.channelKind === kind), (o) => o.day),
    },
  ];
}

// ---------------------------------------------------------------------------
// Metrics
// ---------------------------------------------------------------------------

export const SALES_METRICS: MetricDef[] = [
  {
    id: 'sales.revenue',
    kind: 'metric',
    label: 'Order revenue',
    group: 'Sales',
    description: 'Total value of orders placed (cancelled orders excluded).',
    module: MODULE,
    requires: ['orders'],
    unit: 'currency',
    direction: 'up',
    grain: 'day',
    agg: 'sum',
    series: revenueDaily,
    nonNegative: true,
    available: notConfigured,
  },
  {
    id: 'sales.orders',
    kind: 'metric',
    label: 'Orders',
    group: 'Sales',
    description: 'Orders placed, excluding cancellations.',
    module: MODULE,
    requires: ['orders'],
    unit: 'count',
    direction: 'up',
    grain: 'day',
    agg: 'sum',
    series: ordersDaily,
    nonNegative: true,
    available: notConfigured,
  },
  {
    id: 'sales.aov',
    kind: 'metric',
    label: 'Average order value',
    group: 'Sales',
    description: 'Order revenue divided by order count.',
    module: MODULE,
    requires: ['orders'],
    unit: 'currency',
    direction: 'up',
    grain: 'day',
    agg: 'sum',
    ratio: { num: 'sales.revenue', den: 'sales.orders' },
  },
  {
    id: 'sales.units',
    kind: 'metric',
    label: 'Units sold',
    group: 'Sales',
    description: 'Line-item quantities across orders placed.',
    module: MODULE,
    requires: ['orders'],
    unit: 'count',
    direction: 'up',
    grain: 'day',
    agg: 'sum',
    series: unitsDaily,
    nonNegative: true,
  },
  {
    id: 'sales.units_per_order',
    kind: 'metric',
    label: 'Units per order',
    group: 'Sales',
    description: 'Average basket size in units.',
    module: MODULE,
    requires: ['orders'],
    unit: 'ratio',
    direction: 'up',
    grain: 'day',
    agg: 'sum',
    ratio: { num: 'sales.units', den: 'sales.orders' },
  },
  {
    id: 'sales.customers',
    kind: 'metric',
    label: 'Active customers',
    group: 'Sales',
    description: 'Distinct customers who placed an order in the window.',
    module: MODULE,
    requires: ['orders'],
    unit: 'count',
    direction: 'up',
    grain: 'window',
    agg: 'sum',
    windowValue: (bag, start, end) => (bag.orders ? distinctCustomers(ordersIn(bag, start, end)) : null),
  },
  {
    id: 'sales.new_customers',
    kind: 'metric',
    label: 'First-time customers',
    group: 'Sales',
    description: 'Customers whose first order (in the loaded period) falls inside the window.',
    module: MODULE,
    requires: ['orders'],
    unit: 'count',
    direction: 'up',
    grain: 'window',
    agg: 'sum',
    windowValue: (bag, start, end) => {
      if (!bag.orders) return null;
      const first = new Map<string, string>();
      for (const o of activeOrders(bag)) {
        const key = customerKey(o);
        if (!key) continue;
        const prev = first.get(key);
        if (!prev || o.day < prev) first.set(key, o.day);
      }
      let count = 0;
      for (const day of first.values()) if (inWindow(day, start, end)) count += 1;
      return count;
    },
  },
  {
    id: 'sales.repeat_rate',
    kind: 'metric',
    label: 'Repeat customer rate',
    group: 'Sales',
    description: 'Share of customers in the window who ordered more than once in it.',
    module: MODULE,
    requires: ['orders'],
    unit: 'percent',
    direction: 'up',
    grain: 'window',
    agg: 'sum',
    windowValue: (bag, start, end) => {
      if (!bag.orders) return null;
      const counts = groupCount(ordersIn(bag, start, end), customerKey);
      if (counts.size === 0) return null;
      let repeat = 0;
      for (const n of counts.values()) if (n > 1) repeat += 1;
      return repeat / counts.size;
    },
  },
  {
    id: 'sales.revenue_per_customer',
    kind: 'metric',
    label: 'Revenue per customer',
    group: 'Sales',
    description: 'Order revenue divided by active customers.',
    module: MODULE,
    requires: ['orders'],
    unit: 'currency',
    direction: 'up',
    grain: 'window',
    agg: 'sum',
    ratio: { num: 'sales.revenue', den: 'sales.customers' },
  },
  {
    id: 'sales.cancelled_orders',
    kind: 'metric',
    label: 'Cancelled orders',
    group: 'Sales',
    description: 'Orders that were cancelled.',
    module: MODULE,
    requires: ['orders'],
    unit: 'count',
    direction: 'down',
    grain: 'day',
    agg: 'sum',
    series: (bag) => dailyCount(allOrders(bag).filter((o) => o.status === 'cancelled'), (o) => o.day),
    nonNegative: true,
  },
  {
    id: 'sales.cancel_rate',
    kind: 'metric',
    label: 'Cancellation rate',
    group: 'Sales',
    description: 'Cancelled orders as a share of all orders placed.',
    module: MODULE,
    requires: ['orders'],
    unit: 'percent',
    direction: 'down',
    grain: 'window',
    agg: 'sum',
    windowValue: (bag, start, end) => {
      if (!bag.orders) return null;
      const orders = allOrders(bag).filter((o) => inWindow(o.day, start, end));
      if (orders.length === 0) return null;
      return orders.filter((o) => o.status === 'cancelled').length / orders.length;
    },
  },
  {
    id: 'sales.unshipped_orders',
    kind: 'metric',
    label: 'Unshipped orders',
    group: 'Sales',
    description: 'Orders placed in the loaded period that have not shipped yet.',
    module: MODULE,
    requires: ['orders'],
    unit: 'count',
    direction: 'down',
    grain: 'snapshot',
    agg: 'last',
    snapshot: (bag) => (bag.orders ? allOrders(bag).filter((o) => OPEN_STATUSES.has(o.status)).length : null),
  },
  {
    id: 'sales.returns',
    kind: 'metric',
    label: 'Returns',
    group: 'Sales',
    description: 'Return requests (RMAs) opened.',
    module: MODULE,
    requires: ['returns'],
    unit: 'count',
    direction: 'down',
    grain: 'day',
    agg: 'sum',
    series: (bag) => dailyCount(bag.returns ?? [], (r) => r.day),
    nonNegative: true,
  },
  {
    id: 'sales.return_rate',
    kind: 'metric',
    label: 'Return rate',
    group: 'Sales',
    description: 'Returns opened as a share of orders placed in the same window.',
    module: MODULE,
    requires: ['returns', 'orders'],
    unit: 'percent',
    direction: 'down',
    grain: 'day',
    agg: 'sum',
    ratio: { num: 'sales.returns', den: 'sales.orders' },
  },
  {
    id: 'sales.returned_units',
    kind: 'metric',
    label: 'Units returned',
    group: 'Sales',
    description: 'Quantity across return lines.',
    module: MODULE,
    requires: ['returns'],
    unit: 'count',
    direction: 'down',
    grain: 'day',
    agg: 'sum',
    series: (bag) => dailySeries(bag.returns ?? [], (r) => r.day, (r) => r.units),
    nonNegative: true,
  },
  {
    id: 'sales.refunds',
    kind: 'metric',
    label: 'Refunds issued',
    group: 'Sales',
    description: 'Refund amounts recorded on returns.',
    module: MODULE,
    requires: ['returns'],
    unit: 'currency',
    direction: 'down',
    grain: 'day',
    agg: 'sum',
    series: (bag) => dailySeries(bag.returns ?? [], (r) => r.day, (r) => r.refund_cents ?? 0),
    nonNegative: true,
  },
  {
    id: 'sales.return_value',
    kind: 'metric',
    label: 'Returned merchandise value',
    group: 'Sales',
    description: 'Selling value of the goods on return lines.',
    module: MODULE,
    requires: ['returns'],
    unit: 'currency',
    direction: 'down',
    grain: 'day',
    agg: 'sum',
    series: (bag) => dailySeries(bag.returns ?? [], (r) => r.day, (r) => r.valueCents),
    nonNegative: true,
  },
  ...channelMetric('shopify'),
  ...channelMetric('amazon'),
  ...channelMetric('b2b_portal'),
  ...channelMetric('manual'),
];

// ---------------------------------------------------------------------------
// Breakdowns
// ---------------------------------------------------------------------------

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function productKey(line: { sku: string | null; name: string }): string {
  return line.sku ? `${line.sku} · ${line.name}` : line.name;
}

export const SALES_BREAKDOWNS: BreakdownDef[] = [
  {
    id: 'sales.revenue_by_channel',
    kind: 'breakdown',
    label: 'Revenue by channel',
    group: 'Sales',
    description: 'Order revenue split by sales channel.',
    module: MODULE,
    requires: ['orders'],
    unit: 'currency',
    slices: (bag, start, end) => toSlices(groupSum(ordersIn(bag, start, end), (o) => o.channelName, (o) => o.totalCents)),
  },
  {
    id: 'sales.orders_by_channel',
    kind: 'breakdown',
    label: 'Orders by channel',
    group: 'Sales',
    description: 'Order count split by sales channel.',
    module: MODULE,
    requires: ['orders'],
    unit: 'count',
    slices: (bag, start, end) => toSlices(groupCount(ordersIn(bag, start, end), (o) => o.channelName)),
  },
  {
    id: 'sales.revenue_by_product',
    kind: 'breakdown',
    label: 'Revenue by product',
    group: 'Sales',
    description: 'Line revenue per SKU.',
    module: MODULE,
    requires: ['orders'],
    unit: 'currency',
    slices: (bag, start, end) =>
      toSlices(
        groupSum(
          ordersIn(bag, start, end).flatMap((o) => o.lines),
          productKey,
          (l) => l.unitPriceCents * l.quantity,
        ),
      ),
  },
  {
    id: 'sales.units_by_product',
    kind: 'breakdown',
    label: 'Units by product',
    group: 'Sales',
    description: 'Quantity sold per SKU.',
    module: MODULE,
    requires: ['orders'],
    unit: 'count',
    slices: (bag, start, end) =>
      toSlices(groupSum(ordersIn(bag, start, end).flatMap((o) => o.lines), productKey, (l) => l.quantity)),
  },
  {
    id: 'sales.orders_by_status',
    kind: 'breakdown',
    label: 'Orders by status',
    group: 'Sales',
    description: 'Where orders placed in the window currently sit.',
    module: MODULE,
    requires: ['orders'],
    unit: 'count',
    slices: (bag, start, end) =>
      toSlices(groupCount(allOrders(bag).filter((o) => inWindow(o.day, start, end)), (o) => titleCase(o.status))),
  },
  {
    id: 'sales.revenue_by_customer',
    kind: 'breakdown',
    label: 'Revenue by customer',
    group: 'Sales',
    description: 'Order revenue per customer.',
    module: MODULE,
    requires: ['orders'],
    unit: 'currency',
    slices: (bag, start, end) => toSlices(groupSum(ordersIn(bag, start, end), customerLabel, (o) => o.totalCents)),
  },
  {
    id: 'sales.orders_by_weekday',
    kind: 'breakdown',
    label: 'Orders by weekday',
    group: 'Sales',
    description: 'Which days of the week orders come in.',
    module: MODULE,
    requires: ['orders'],
    unit: 'count',
    slices: (bag, start, end) => {
      const counts = groupCount(ordersIn(bag, start, end), (o) => WEEKDAYS[new Date(`${o.day}T00:00:00Z`).getUTCDay()]);
      return WEEKDAYS.filter((d) => counts.has(d)).map((d) => ({ label: d, value: counts.get(d) ?? 0 }));
    },
  },
  {
    id: 'sales.returns_by_reason',
    kind: 'breakdown',
    label: 'Returns by reason',
    group: 'Sales',
    description: 'Why customers are sending things back.',
    module: MODULE,
    requires: ['returns'],
    unit: 'count',
    slices: (bag, start, end) =>
      toSlices(groupCount((bag.returns ?? []).filter((r) => inWindow(r.day, start, end)), (r) => titleCase(r.reason_code))),
  },
  {
    id: 'sales.returns_by_channel',
    kind: 'breakdown',
    label: 'Returns by channel',
    group: 'Sales',
    description: 'Return requests split by the channel the order came from.',
    module: MODULE,
    requires: ['returns'],
    unit: 'count',
    slices: (bag, start, end) =>
      toSlices(
        groupCount(
          (bag.returns ?? []).filter((r) => inWindow(r.day, start, end)),
          (r) => CHANNEL_LABELS[r.channel_kind] ?? titleCase(r.channel_kind),
        ),
      ),
  },
];

// ---------------------------------------------------------------------------
// Tables
// ---------------------------------------------------------------------------

export const SALES_TABLES: TableDef[] = [
  {
    id: 'sales.top_products',
    kind: 'table',
    label: 'Top products',
    group: 'Sales',
    description: 'Best sellers by revenue in the window.',
    module: MODULE,
    requires: ['orders'],
    columns: [
      { key: 'sku', label: 'SKU' },
      { key: 'name', label: 'Product' },
      { key: 'units', label: 'Units', unit: 'count' },
      { key: 'orders', label: 'Orders', unit: 'count' },
      { key: 'revenue', label: 'Revenue', unit: 'currency' },
    ],
    defaultColumns: ['name', 'units', 'orders', 'revenue'],
    defaultSort: { key: 'revenue', dir: 'desc' },
    defaultLimit: 10,
    rows: (bag, start, end) => {
      const acc = new Map<string, { sku: string | null; name: string; units: number; orders: number; revenue: number }>();
      for (const order of ordersIn(bag, start, end)) {
        const seen = new Set<string>();
        for (const line of order.lines) {
          const key = productKey(line);
          const row = acc.get(key) ?? { sku: line.sku, name: line.name, units: 0, orders: 0, revenue: 0 };
          row.units += line.quantity;
          row.revenue += line.unitPriceCents * line.quantity;
          if (!seen.has(key)) {
            row.orders += 1;
            seen.add(key);
          }
          acc.set(key, row);
        }
      }
      return Array.from(acc.entries()).map(([key, r]) => ({
        key,
        cells: { sku: r.sku ?? '—', name: r.name, units: r.units, orders: r.orders, revenue: r.revenue },
      }));
    },
  },
  {
    id: 'sales.top_customers',
    kind: 'table',
    label: 'Top customers',
    group: 'Sales',
    description: 'Customers ranked by order revenue in the window.',
    module: MODULE,
    requires: ['orders'],
    columns: [
      { key: 'customer', label: 'Customer' },
      { key: 'orders', label: 'Orders', unit: 'count' },
      { key: 'revenue', label: 'Revenue', unit: 'currency' },
      { key: 'aov', label: 'Avg order', unit: 'currency' },
      { key: 'last', label: 'Last order', date: true },
    ],
    defaultSort: { key: 'revenue', dir: 'desc' },
    defaultLimit: 10,
    rows: (bag, start, end) => {
      const acc = new Map<string, { label: string; orders: number; revenue: number; last: string }>();
      for (const o of ordersIn(bag, start, end)) {
        const key = customerKey(o) ?? `guest:${o.id}`;
        const row = acc.get(key) ?? { label: customerLabel(o), orders: 0, revenue: 0, last: o.day };
        row.orders += 1;
        row.revenue += o.totalCents;
        if (o.day > row.last) row.last = o.day;
        acc.set(key, row);
      }
      return Array.from(acc.entries()).map(([key, r]) => ({
        key,
        cells: { customer: r.label, orders: r.orders, revenue: r.revenue, aov: r.orders ? r.revenue / r.orders : null, last: r.last },
      }));
    },
  },
  {
    id: 'sales.recent_orders',
    kind: 'table',
    label: 'Recent orders',
    group: 'Sales',
    description: 'Latest orders placed in the window.',
    module: MODULE,
    requires: ['orders'],
    columns: [
      { key: 'so', label: 'Order' },
      { key: 'day', label: 'Date', date: true },
      { key: 'customer', label: 'Customer' },
      { key: 'channel', label: 'Channel' },
      { key: 'status', label: 'Status' },
      { key: 'total', label: 'Total', unit: 'currency' },
    ],
    defaultColumns: ['so', 'day', 'customer', 'status', 'total'],
    defaultSort: { key: 'day', dir: 'desc' },
    defaultLimit: 8,
    rows: (bag, start, end) =>
      allOrders(bag)
        .filter((o) => inWindow(o.day, start, end))
        .map((o) => ({
          key: o.id,
          href: '/sales/orders',
          cells: {
            so: o.soNumber,
            day: o.day,
            customer: customerLabel(o),
            channel: o.channelName,
            status: titleCase(o.status),
            total: o.totalCents,
          },
        })),
  },
  {
    id: 'sales.channel_summary',
    kind: 'table',
    label: 'Channel summary',
    group: 'Sales',
    description: 'Orders, revenue and average order value per channel.',
    module: MODULE,
    requires: ['orders'],
    columns: [
      { key: 'channel', label: 'Channel' },
      { key: 'orders', label: 'Orders', unit: 'count' },
      { key: 'revenue', label: 'Revenue', unit: 'currency' },
      { key: 'aov', label: 'Avg order', unit: 'currency' },
      { key: 'share', label: 'Share', unit: 'percent' },
    ],
    defaultSort: { key: 'revenue', dir: 'desc' },
    defaultLimit: 10,
    rows: (bag, start, end) => {
      const orders = ordersIn(bag, start, end);
      const total = orders.reduce((s, o) => s + o.totalCents, 0);
      const acc = new Map<string, { orders: number; revenue: number }>();
      for (const o of orders) {
        const row = acc.get(o.channelName) ?? { orders: 0, revenue: 0 };
        row.orders += 1;
        row.revenue += o.totalCents;
        acc.set(o.channelName, row);
      }
      return Array.from(acc.entries()).map(([channel, r]) => ({
        key: channel,
        cells: {
          channel,
          orders: r.orders,
          revenue: r.revenue,
          aov: r.orders ? r.revenue / r.orders : null,
          share: total ? r.revenue / total : null,
        },
      }));
    },
  },
  {
    id: 'sales.returns_table',
    kind: 'table',
    label: 'Recent returns',
    group: 'Sales',
    description: 'Return requests opened in the window.',
    module: MODULE,
    requires: ['returns'],
    columns: [
      { key: 'rma', label: 'RMA' },
      { key: 'day', label: 'Requested', date: true },
      { key: 'channel', label: 'Channel' },
      { key: 'reason', label: 'Reason' },
      { key: 'resolution', label: 'Resolution' },
      { key: 'status', label: 'Status' },
      { key: 'units', label: 'Units', unit: 'count' },
      { key: 'refund', label: 'Refund', unit: 'currency' },
    ],
    defaultColumns: ['rma', 'day', 'reason', 'status', 'refund'],
    defaultSort: { key: 'day', dir: 'desc' },
    defaultLimit: 8,
    rows: (bag, start, end) =>
      (bag.returns ?? [])
        .filter((r) => inWindow(r.day, start, end))
        .map((r) => ({
          key: r.id,
          href: '/sales/returns',
          cells: {
            rma: r.rma_number,
            day: r.day,
            channel: CHANNEL_LABELS[r.channel_kind] ?? titleCase(r.channel_kind),
            reason: titleCase(r.reason_code),
            resolution: titleCase(r.resolution),
            status: titleCase(r.status),
            units: r.units,
            refund: r.refund_cents,
          },
        })),
  },
];

// ---------------------------------------------------------------------------
// Forecast sources
// ---------------------------------------------------------------------------

function planSalesRevenueMonthly(bag: DatasetBag): SeriesPoint[] {
  return monthlySeries(bag.projections_sales ?? [], (r) => r.month, (r) => r.units * r.unit_price_cents);
}

function planSalesUnitsMonthly(bag: DatasetBag, sku?: string | null): SeriesPoint[] {
  const rows = (bag.projections_sales ?? []).filter((r) => !sku || r.sku === sku);
  return monthlySeries(rows, (r) => r.month, (r) => r.units);
}

function skuForItem(bag: DatasetBag, itemId: string | null | undefined): string | null {
  if (!itemId) return null;
  return bag.items?.find((i) => i.id === itemId)?.sku ?? null;
}

function itemUnitsMonthly(bag: DatasetBag, itemId?: string | null): SeriesPoint[] {
  const sku = skuForItem(bag, itemId);
  if (!sku) return [];
  const out = new Map<string, number>();
  for (const order of activeOrders(bag)) {
    for (const line of order.lines) {
      if (line.sku !== sku) continue;
      const m = monthKey(order.day);
      out.set(m, (out.get(m) ?? 0) + line.quantity);
    }
  }
  return toSeries(out);
}

function externalItemForecast(bag: DatasetBag, itemId?: string | null): ForecastPoint[] {
  if (!itemId || !bag.forecast_runs) return [];
  const byMonth = new Map<string, { v: number; lo: number; hi: number }>();
  for (const row of bag.forecast_runs.rows) {
    if (row.item_id !== itemId) continue;
    const m = monthKey(row.for_date);
    const acc = byMonth.get(m) ?? { v: 0, lo: 0, hi: 0 };
    acc.v += row.predicted_qty;
    acc.lo += row.lower_bound ?? row.predicted_qty;
    acc.hi += row.upper_bound ?? row.predicted_qty;
    byMonth.set(m, acc);
  }
  return Array.from(byMonth.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([t, p]) => ({ t, ...p }));
}

export const SALES_FORECASTS: ForecastSourceDef[] = [
  {
    id: 'fc.sales_revenue',
    kind: 'forecast',
    label: 'Order revenue',
    group: 'Sales',
    description: 'Monthly order revenue projected forward, with the sales plan (units × price) for comparison.',
    module: MODULE,
    requires: ['orders', 'projections_sales'],
    unit: 'currency',
    nonNegative: true,
    history: (bag) => monthlySeries(activeOrders(bag), (o) => o.day, (o) => o.totalCents),
    manual: planSalesRevenueMonthly,
    manualLabel: 'Sales plan',
  },
  {
    id: 'fc.sales_orders',
    kind: 'forecast',
    label: 'Orders',
    group: 'Sales',
    description: 'Monthly order count projected forward.',
    module: MODULE,
    requires: ['orders'],
    unit: 'count',
    nonNegative: true,
    history: (bag) => monthlySeries(activeOrders(bag), (o) => o.day, () => 1),
  },
  {
    id: 'fc.sales_units',
    kind: 'forecast',
    label: 'Units sold',
    group: 'Sales',
    description: 'Monthly units across all products projected forward, with planned units for comparison.',
    module: MODULE,
    requires: ['orders', 'projections_sales'],
    unit: 'count',
    nonNegative: true,
    history: (bag) => monthlySeries(activeOrders(bag), (o) => o.day, lineUnits),
    manual: (bag) => planSalesUnitsMonthly(bag),
    manualLabel: 'Sales plan',
  },
  {
    id: 'fc.item_demand',
    kind: 'forecast',
    label: 'Item demand',
    group: 'Sales',
    description:
      'Monthly units for one SKU projected forward. Overlays the latest demand-forecast run and the planned units for that SKU.',
    module: MODULE,
    requires: ['orders', 'items', 'forecast_runs', 'projections_sales'],
    unit: 'count',
    nonNegative: true,
    perItem: true,
    history: itemUnitsMonthly,
    manual: (bag, itemId) => planSalesUnitsMonthly(bag, skuForItem(bag, itemId)),
    manualLabel: 'Sales plan',
    external: externalItemForecast,
    externalLabel: 'Demand model run',
  },
  {
    id: 'fc.customers',
    kind: 'forecast',
    label: 'Active customers',
    group: 'Sales',
    description: 'Distinct ordering customers per month projected forward.',
    module: MODULE,
    requires: ['orders'],
    unit: 'count',
    nonNegative: true,
    history: (bag) => {
      const byMonth = new Map<string, Set<string>>();
      for (const o of activeOrders(bag)) {
        const key = customerKey(o);
        if (!key) continue;
        const m = monthKey(o.day);
        const set = byMonth.get(m) ?? new Set<string>();
        set.add(key);
        byMonth.set(m, set);
      }
      return Array.from(byMonth.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([t, set]) => ({ t, v: set.size }));
    },
  },
];

/** Line revenue helper exported for cross-domain metrics (marketing MER / CAC). */
export { lineRevenue, activeOrders, ordersIn };
